import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/theme";

function Field({ label, required, children, full }) {
  return (
    <div className={`space-y-1.5 ${full ? "sm:col-span-2" : ""}`}>
      <Label className="text-xs font-semibold text-slate-600">{label}{required && <span className="text-red-500"> *</span>}</Label>
      {children}
    </div>
  );
}

export function TaskFormDialog({ open, onOpenChange, task, defaultLead, onSaved }) {
  const { meta, user } = useAuth();
  const [form, setForm] = useState({ title: "", lead_id: "", assigned_to: "", due_date: "", priority: "Medium", status: "Pending", notes: "" });
  const [leads, setLeads] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const editing = !!task;

  useEffect(() => {
    if (open) {
      setErr("");
      if (task) setForm({ ...task, lead_id: task.lead_id || "", assigned_to: task.assigned_to || "", due_date: (task.due_date || "").slice(0, 10) });
      else setForm({ title: "", lead_id: defaultLead?.id || "", assigned_to: user?.id || "", due_date: "", priority: "Medium", status: "Pending", notes: "" });
      api.get("/leads?limit=200").then((r) => setLeads(r.data.items)).catch(() => {});
    }
  }, [open, task, defaultLead, user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.title.trim()) { setErr("Task title is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, lead_id: form.lead_id || null, assigned_to: form.assigned_to || null, due_date: form.due_date || null };
      if (editing) { await api.put(`/tasks/${task.id}`, payload); toast.success("Task updated"); }
      else { await api.post("/tasks", payload); toast.success("Task created"); }
      onOpenChange(false); onSaved && onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg gap-0 p-0 overflow-hidden flex flex-col max-h-[92vh]">
        <DialogHeader className="border-b border-slate-100 px-6 py-4"><DialogTitle className="text-lg">{editing ? "Edit Task" : "New Task"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 gap-4 overflow-y-auto px-6 py-5 sm:grid-cols-2">
          <Field label="Task Title" required full>
            <Input data-testid="task-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Send flooring proposal" />
            {err && <p className="text-xs text-red-500">{err}</p>}
          </Field>
          {!defaultLead && (
            <Field label="Related Lead" full>
              <Select value={form.lead_id || "none"} onValueChange={(v) => set("lead_id", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="task-lead"><SelectValue placeholder="Select lead" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No lead</SelectItem>
                  {leads.map((l) => <SelectItem key={l.id} value={l.id}>{l.company_name} — {l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          )}
          <Field label="Assigned To">
            <Select value={form.assigned_to} onValueChange={(v) => set("assigned_to", v)}>
              <SelectTrigger data-testid="task-assigned"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{(meta?.users || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {ROLE_LABELS[u.role]}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Due Date"><Input data-testid="task-due" type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} /></Field>
          <Field label="Priority">
            <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
              <SelectTrigger data-testid="task-priority"><SelectValue /></SelectTrigger>
              <SelectContent>{(meta?.priorities || []).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Status">
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="task-status"><SelectValue /></SelectTrigger>
              <SelectContent>{["Pending", "In Progress", "Completed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Notes" full><Textarea data-testid="task-notes" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-100 px-6 py-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="task-submit" onClick={submit} disabled={saving} className="font-semibold">{saving ? "Saving…" : editing ? "Save" : "Create Task"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
